import { useCallback, useEffect, useRef, useState } from 'react';
import { Minus, Plus, RotateCw } from 'lucide-react';

/**
 * Visor de PDF propio, dibujado con pdf.js sobre un canvas.
 *
 * La alternativa era un `<iframe>` apuntando al archivo y dejar que el
 * navegador lo resolviera. No sirve: el visor integrado no existe en varios
 * webviews y navegadores corporativos —el propio navegador embebido de estas
 * herramientas ofrece descargar el PDF en vez de mostrarlo—, así que la vista
 * previa fallaría justo donde hay que demostrarla. Dibujándolo nosotros se ve
 * igual en todas partes, y de paso los documentos de Office convertidos a PDF
 * usan este mismo camino.
 */

/** Más allá de esto, una memoria de 300 páginas colgaría la pestaña. */
const MAXIMO_PAGINAS = 40;

interface Props {
  url: string;
  /** Se avisa hacia arriba para poder mostrar el error con el botón de descarga. */
  alFallar(mensaje: string): void;
}

export function VistaPDF({ url, alFallar }: Props) {
  const contenedor = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1.25);
  const [paginas, setPaginas] = useState(0);
  const [cargando, setCargando] = useState(true);

  const dibujar = useCallback(async (): Promise<{ destroy(): Promise<void> } | null> => {
    const caja = contenedor.current;
    if (!caja) return null;

    setCargando(true);
    // pdf.js pesa; se carga solo cuando alguien abre un PDF, no en el arranque.
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;

    // En pdf.js el que se destruye es la tarea de carga, no el documento: es
    // la que tiene el worker colgando.
    const tarea = pdfjs.getDocument({ url, withCredentials: false });
    const documento = await tarea.promise;
    setPaginas(documento.numPages);

    caja.replaceChildren();
    const total = Math.min(documento.numPages, MAXIMO_PAGINAS);
    // La pantalla puede ser retina: se dibuja al doble de píxeles y se muestra
    // al tamaño lógico, si no el texto sale borroso.
    const densidad = Math.min(window.devicePixelRatio || 1, 2);

    for (let numero = 1; numero <= total; numero++) {
      const pagina = await documento.getPage(numero);
      const vista = pagina.getViewport({ scale: escala });
      const lienzo = document.createElement('canvas');
      lienzo.className = 'visor-pdf__pagina';
      lienzo.width = Math.floor(vista.width * densidad);
      lienzo.height = Math.floor(vista.height * densidad);
      lienzo.style.width = `${Math.floor(vista.width)}px`;
      lienzo.style.height = `${Math.floor(vista.height)}px`;
      caja.appendChild(lienzo);

      const contexto = lienzo.getContext('2d');
      if (!contexto) continue;
      contexto.scale(densidad, densidad);
      await pagina.render({ canvas: lienzo, canvasContext: contexto, viewport: vista }).promise;
    }

    if (documento.numPages > MAXIMO_PAGINAS) {
      const aviso = document.createElement('p');
      aviso.className = 'visor-pdf__aviso';
      aviso.textContent = `Se muestran las primeras ${MAXIMO_PAGINAS} páginas de ${documento.numPages}. Descarga el archivo para verlo completo.`;
      caja.appendChild(aviso);
    }

    setCargando(false);
    // Queda viva mientras se ve el documento; se libera al desmontar.
    return tarea;
  }, [url, escala]);

  useEffect(() => {
    let vigente = true;
    let abierto: { destroy(): Promise<void> } | null = null;

    dibujar()
      .then((documento) => {
        abierto = documento;
        // Si el visor se cerró mientras se dibujaba, el documento igual hay
        // que soltarlo: pdf.js mantiene un worker por documento abierto.
        if (!vigente) void documento?.destroy();
      })
      .catch((error) => {
        if (!vigente) return;
        setCargando(false);
        alFallar(error instanceof Error ? error.message : 'No se pudo dibujar el documento');
      });

    return () => {
      vigente = false;
      void abierto?.destroy();
    };
  }, [dibujar, alFallar]);

  return (
    <div className="visor-pdf">
      <div className="visor-pdf__barra">
        <button className="btn btn--fantasma btn--icono btn--sm" title="Alejar" onClick={() => setEscala((e) => Math.max(0.5, +(e - 0.25).toFixed(2)))}>
          <Minus size={14} />
        </button>
        <span className="visor-pdf__zoom">{Math.round(escala * 100)}%</span>
        <button className="btn btn--fantasma btn--icono btn--sm" title="Acercar" onClick={() => setEscala((e) => Math.min(3, +(e + 0.25).toFixed(2)))}>
          <Plus size={14} />
        </button>
        <button className="btn btn--fantasma btn--icono btn--sm" title="Tamaño original" onClick={() => setEscala(1.25)}>
          <RotateCw size={13} />
        </button>
        <span className="visor-pdf__paginas">{cargando ? 'Dibujando…' : `${Math.min(paginas, MAXIMO_PAGINAS)} de ${paginas} página(s)`}</span>
      </div>
      <div className="visor-pdf__hojas" ref={contenedor} />
    </div>
  );
}
